import React, { useRef, useEffect, useState, useMemo } from 'react';
import { ReduxState, SaveStatus, RentalsTabValues } from 'services/types';
import { connect, useSelector } from 'react-redux';

import Container from 'components/Container';
import { Theme, Grid, Button, Hidden } from '@mui/material';
import { WithStyles } from '@mui/styles';
import createStyles from '@mui/styles/createStyles';
import withStyles from '@mui/styles/withStyles';
import ShopperCard from './ShopperCard';
import SideBar from './SideBar';
import ReactToPrint from 'react-to-print';
import Receipt from 'components/Receipt';
import BarCodeReader from 'components/BarCodeReader';
import ParticipantsBar from './ParticipantsBar';
import { sortShoppers } from 'common/utils/rentalUtils';
import SaveFeedback from './SaveFeedback';
import SetPageNavAction from 'components/Portals/SetPageNavAction';
import SetPageHeaderRightContent from 'components/Portals/SetPageHeaderRightContent';

import { Routes } from 'routing';
import * as ViewSelectors from 'selectors/ViewSelectors';
import * as StockSelectors from 'selectors/StockSelectors';
import { useTranslation } from 'services/localization/useTranslation';
import { getNewOrderNumber } from 'common/api/db/functions';
import { getResponsiblePersonDetails, newFirestoreId } from 'common/utils/newRentalUtils';
import { OrderObject, SetProductWithParentId, Shop, PartialOrder, Shopper } from 'common/types';
import { addOrderPaymentRequest, getNewPaymentNumber, isValidOrderId } from 'common/api/frontend';
import {
	setStartPrint,
	updateActiveShopperId,
	updateSelectedSetProducts,
	updateSelectedNonSetProductIds,
	updateActivePartialOrder,
	openMobileRentalSideBar,
} from 'actions';

import { buildRentalsOfTypeData, RentalsType } from 'selectors/RentalsSelectors';
import { getOrderPayments } from 'common/modules/atoms/paymentAmounts';
import { Notification, showNotification } from 'actions/NotificationActions';
import {
	getExternalPosTriggerTypes,
	handlePosIntegrationAction,
} from 'common/modules/posIntegrations';
import { getReceiptBarCode } from 'services/utils/receipt';
import { getDefaultOrderBarcode } from 'common/modules/orderReceipts/utils';
import { getDefaultCurrencyObject } from 'common/utils/currencyUtils';
import { AutoSizer, CellMeasurer, CellMeasurerCache, List, ListRowProps } from 'react-virtualized';

const DEFAULT_SHOPPER_CARD_HEIGHT = 400;
interface OwnProps {
	rental: OrderObject;
}

interface DispatchProps {
	setStartPrint: (startPrint: boolean) => void;
	updateActiveShopperId: (activeShopperId: string | null) => void;
	updateSelectedSetProducts: (selectedSetProducts: SetProductWithParentId[]) => void;
	updateSelectedNonSetProductIds: (selectedNonSetProductIds: string[]) => void;
	updateActivePartialOrder: (partialOrder: PartialOrder) => void;
	openMobileRentalSideBar: () => void;
	showNotification: (notification: Notification) => void;
}

interface StateToProps {
	shop: Shop;
	startPrint: boolean;
	activeShopperId: string | null;
	selectedNonSetProductIds: string[];
	selectedSetProducts: SetProductWithParentId[];
	saveStatus: SaveStatus;
	showEarlyOrLatePickupDialog: boolean;
}

type Props = StateToProps & DispatchProps & OwnProps & WithStyles<typeof styles>;

const RentalView = (props: Props) => {
	const { classes, rental, shop, startPrint, activeShopperId, saveStatus } = props;
	const { rentalInfo, products, shoppers, orderDelivery } = rental;
	const { publicInfo, readOnly, shopInfo } = shop;
	const { id: orderId, orderNumber } = rentalInfo;
	const amountToPay = getOrderPayments(rentalInfo).charge.unpaid;
	const currency =
		rentalInfo.pricing.currency ?? publicInfo.currency.code ?? getDefaultCurrencyObject().code;
	const { t } = useTranslation();
	const virtualListRef = useRef<HTMLDivElement | null>(null);
	const categories = useSelector(StockSelectors.categoriesData);
	const rentalsActiveTab = useSelector(ViewSelectors.rentalsActiveTab);
	const bookedRentals = useSelector(buildRentalsOfTypeData(RentalsType.BOOKED));
	const activeRentals = useSelector(buildRentalsOfTypeData(RentalsType.ACTIVE));
	const expiredRentals = useSelector(buildRentalsOfTypeData(RentalsType.EXPIRED));

	const [scannedCode, setScannedCode] = useState({ scannedCode: '' });
	const [paymentNumber, setPaymentNumber] = useState<number | null>(null);
	const [printContentPromise, setPrintContentPromise] = useState<{ resolve?: () => void }>({});
	const [receiptBarCode, setReceiptBarCode] = useState<string | null>(null);
	const receiptRef = useRef<HTMLDivElement | null>(null);
	const externalPosTriggerTypes = getExternalPosTriggerTypes({
		readOnly,
		shopInfo,
	});
	const onOrderPrintPosTriggerInUse = externalPosTriggerTypes.includes('onOrderReceiptPrint');
	const sortedShoppers = sortShoppers(shoppers, rentalInfo.responsiblePerson);

	const cache = useMemo(
		() =>
			new CellMeasurerCache({
				fixedWidth: true,
				defaultHeight: DEFAULT_SHOPPER_CARD_HEIGHT,
			}),
		[],
	);

	const cameFromSkidataView = rentalsActiveTab === RentalsTabValues.SKIDATA;

	const pageNavigationBackAction = {
		to: cameFromSkidataView
			? Routes.SKIDATA
			: Routes.BOOKINGS + '/' + rentalsActiveTab.toLowerCase(),
		title: t('headerBar.orders', 'Orders'),
	};

	const rentals = useMemo(() => [...bookedRentals, ...activeRentals, ...expiredRentals], [
		bookedRentals,
		activeRentals,
		expiredRentals,
	]);

	useEffect(() => {
		const recalculateVirtualizedList = () => {
			cache.clearAll();
		};
		window.addEventListener('resize', recalculateVirtualizedList);
		return () => {
			window.removeEventListener('resize', recalculateVirtualizedList);
		};
	}, [cache]);

	useEffect(() => {
		const printButton = document.getElementById('print-button');
		if (startPrint && printButton) {
			printButton.click();
		}
	}, [startPrint]);

	// This is to make sure all relevant content is loaded before we trigger receipt printing
	useEffect(() => {
		if (
			printContentPromise.resolve &&
			receiptBarCode != null &&
			paymentNumber != null &&
			orderNumber != null
		) {
			printContentPromise.resolve && printContentPromise.resolve();
			setPrintContentPromise({});
		}
	}, [printContentPromise, receiptBarCode, paymentNumber, orderNumber]);

	const handlePrintingDone = (receiptBarCode: string | null, paymentNumber: number) => {
		if (onOrderPrintPosTriggerInUse) {
			const paymentRequestId = newFirestoreId();
			const responsiblePerson = getResponsiblePersonDetails(rentalInfo.responsiblePerson, shoppers);
			addOrderPaymentRequest({
				id: paymentRequestId,
				shopId: shop.shopInfo.id,
				orderId,
				amount: {
					value: amountToPay,
					currency,
				},
				paymentNumber,
				shopperIds: shoppers.map((s) => s.id),
				productIds: products.map((p) => p.id),
				orderDeliveryId: orderDelivery?.id,
				paid: false,
			});
			if (receiptBarCode) {
				handlePosIntegrationAction(
					{
						shopId: shop.shopInfo.id,
						shopReadOnly: shop.readOnly,
						locationId: rentalInfo.startLocation.id,
						customer: {
							firstName: responsiblePerson.firstName,
							lastName: responsiblePerson.lastName,
						},
						orderDate: rentalInfo.startDate,
						receiptProducts: products,
						barCode: receiptBarCode,
						paymentNumber,
						paymentRequestId,
						categories,
					},
					t,
				);
			}
		}
		props.setStartPrint(false);
		setPaymentNumber(null);
		setReceiptBarCode(null);
	};

	useEffect(() => {
		if (!activeShopperId) {
			const sortedShoppers = sortShoppers(shoppers, rentalInfo.responsiblePerson);
			props.updateActiveShopperId(sortedShoppers[0].id);
		}
		return () => {
			props.updateActiveShopperId(null);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const handleBarCodeRead = async (p: string) => {
		if (!(await isValidOrderId(p, shop.shopInfo.id, rentals))) {
			setScannedCode({ scannedCode: p.toUpperCase() });
		}
	};

	const handleScrollToShopper = (shopperId: string) => {
		const shopperIndex = sortedShoppers.findIndex((s) => s.id === shopperId);
		(virtualListRef.current as any)?.scrollToRow?.(shopperIndex);
	};

	const renderShopperCard = (
		args: ListRowProps & { rental: OrderObject; shopper: Shopper; shopperIndex: number },
	) => {
		const { index, rental, shopper, key, style, parent, shopperIndex } = args;
		const shopperProducts = products.filter((p) => p.shopperId === shopper.id);
		return (
			<CellMeasurer cache={cache} columnIndex={0} key={key} rowIndex={index} parent={parent}>
				{({ measure, registerChild }) => (
					<div ref={registerChild} key={key} style={style}>
						<ShopperCard
							shopper={shopper}
							shoppers={shoppers}
							shopperProducts={shopperProducts}
							shopperIndex={shopperIndex}
							rental={rental}
							allOrderProducts={products}
							shopPublicInfo={publicInfo}
							scanObject={scannedCode}
							remeasureVirtualizedRow={measure}
						/>
					</div>
				)}
			</CellMeasurer>
		);
	};

	const renderParticipantBar = (args: ListRowProps) => {
		const { index, key, style, parent } = args;
		return (
			<CellMeasurer cache={cache} columnIndex={0} key={key} rowIndex={index} parent={parent}>
				{({ measure, registerChild }) => (
					<div ref={registerChild} key={key} style={style}>
						<Grid container justifyContent="space-between" alignItems="center" spacing={2}>
							<Grid item>
								<SaveFeedback status={saveStatus} retrySave={updateOrder} />
							</Grid>
							<Grid item className={classes.participantsBarContainer}>
								<ParticipantsBar
									rental={rental}
									shoppers={sortedShoppers}
									scrollToShopper={handleScrollToShopper}
									responsiblePerson={rentalInfo.responsiblePerson}
								/>
							</Grid>
						</Grid>
					</div>
				)}
			</CellMeasurer>
		);
	};

	const updateOrder = () => {
		const { rentalInfo, products, shoppers } = props.rental;
		props.updateActivePartialOrder({
			rentalInfo: {
				id: rentalInfo.id,
				data: rentalInfo,
			},
			products: products.map((p) => ({
				id: p.id,
				data: p,
			})),
			shoppers: shoppers.map((s) => ({
				id: s.id,
				data: s,
			})),
		});
	};

	const handlePrintContentUpdate = async () => {
		const orderNumber =
			rentalInfo.orderNumber ??
			(await getNewOrderNumber({ orderId: rentalInfo.id, shopId: rentalInfo.shopId }));
		if (paymentNumber == null) {
			const newPaymentNumber = await getNewPaymentNumber(rentalInfo.shopId);
			setPaymentNumber(newPaymentNumber);
			setReceiptBarCode(
				getReceiptBarCode({
					readOnlyData: readOnly,
					shopInfo,
					paymentNumber: newPaymentNumber,
					amount: amountToPay,
					orderNumber: orderNumber,
					orderId,
					receiptType: 'order',
				}),
			);
		}
	};

	const virtualListRowRenderer = (props: ListRowProps) => {
		const { index, ...rest } = props;
		if (index === 0) {
			return renderParticipantBar({ index, ...rest });
		}
		// Index 0 = participant bar, so index 1 = 1st shopper
		const shopperIndex = index - 1;
		const shopper = sortedShoppers[shopperIndex];
		return renderShopperCard({
			rental,
			shopper,
			shopperIndex,
			index,
			...rest,
		});
	};

	return (
		<>
			<SetPageNavAction priority={2} backAction={pageNavigationBackAction} />
			<SetPageHeaderRightContent priority={2}>
				<Hidden mdUp implementation="css">
					<Button
						variant="contained"
						color="primary"
						onClick={() => props.openMobileRentalSideBar()}
					>
						{t('common:menu', 'Menu')}
					</Button>
				</Hidden>
			</SetPageHeaderRightContent>
			<BarCodeReader barCodeRead={(p) => handleBarCodeRead(p)} caseSensitive>
				<div className={classes.rentalViewContainer}>
					<Container className={classes.shopperCardContainer}>
						<AutoSizer>
							{({ height, width }) => (
								<List
									//@ts-ignore
									ref={virtualListRef}
									estimatedRowSize={DEFAULT_SHOPPER_CARD_HEIGHT}
									deferredMeasurementCache={cache}
									height={height}
									width={width}
									overscanRowCount={4}
									rowCount={shoppers.length + 1} // Shoppers + participant bar
									rowHeight={cache.rowHeight}
									rowRenderer={virtualListRowRenderer}
									scrollToAlignment="start"
									style={{
										padding: '20px 20px 64px 20px',
										outline: 'none',
									}}
								/>
							)}
						</AutoSizer>
						<>
							<ReactToPrint
								trigger={() => (
									<button id="print-button" style={{ display: 'none' }}>
										{'Print'}
									</button>
								)}
								onBeforeGetContent={() => {
									handlePrintContentUpdate();
									return new Promise<void>((resolve) => setPrintContentPromise({ resolve }));
								}}
								content={() => receiptRef.current!}
								onAfterPrint={() => handlePrintingDone(receiptBarCode, paymentNumber || 0)}
							/>
							<div style={{ display: 'none' }}>
								<Receipt
									rental={rental}
									shop={shop}
									ref={receiptRef as any}
									barCode={receiptBarCode ?? getDefaultOrderBarcode(orderNumber, orderId)}
								/>
							</div>
						</>
					</Container>
					<SideBar />
				</div>
			</BarCodeReader>
		</>
	);
};

const styles = (theme: Theme) =>
	createStyles({
		topSpacing: {
			marginTop: theme.spacing(2),
		},
		shopperCardContainer: {
			flexGrow: 1,
			height: '100%',
			overflowX: 'auto',
			padding: 0,
		},
		participantsBarContainer: {
			overflow: 'auto',
		},
		rentalViewContainer: {
			display: 'flex',
			height: 'calc(100vh - 56px - 32px)', // Height of header bar and status bar
			[theme.breakpoints.up('sm')]: {
				height: 'calc(100vh - 64px - 32px)', // Height of header bar and status bar
			},
		},
		arrowForward: {
			marginLeft: theme.spacing(1),
		},
		'@global': {
			'.ReactVirtualized__Grid__innerScrollContainer': {
				overflow: 'visible !important',
			},
		},
	});

const mapStateToProps = ({ shop, view, rentalView }: ReduxState): StateToProps => {
	const activeShop = shop.shop.data!;
	return {
		startPrint: view.startPrint,
		shop: activeShop,
		activeShopperId: rentalView.activeShopperId,
		selectedNonSetProductIds: rentalView.selectedNonSetProductIds,
		selectedSetProducts: rentalView.selectedSetProducts,
		saveStatus: rentalView.saveStatus,
		showEarlyOrLatePickupDialog: view.showEarlyOrLatePickupDialog,
	};
};

const dispatchProps: DispatchProps = {
	setStartPrint,
	updateActiveShopperId,
	updateSelectedSetProducts,
	updateSelectedNonSetProductIds,
	updateActivePartialOrder,
	openMobileRentalSideBar,
	showNotification,
};

export default withStyles(styles)(connect(mapStateToProps, dispatchProps)(RentalView));
